import React, { useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  hideModal,
  showModal,
} from "../../../../connected-components/modal/reducer";
import { ModalType } from "../../../../shared/utils/enums";
import { User, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { OverviewTab } from "./tabs/OverviewTab";
import { VitalsTab } from "./tabs/VitalsTab";
import { ReportsTab } from "./tabs/ReportsTab";
import MedicationsTab from "./tabs/MedicationsTab";
import { ClinicalNotesTab } from "./tabs/ClinicalNotesTab";
import { MedicalHistoryTab } from "./tabs/MedicalHistoryTab";
import { LabResultsTab } from "./tabs/LabResultsTab";
import { ImmunizationsTab } from "./tabs/ImmunizationsTab";
import { BillingTab } from "./tabs/BillingTab";
import { OrdersTab } from "./tabs/OrdersTab";
import { AISidebar } from "./components/AISidebar";
import {
  Patient,
  PatientDetailsPageProps,
  Medication,
} from "../../../../interfaces/screens/content/patientDetails";
import logo from "../../../../assets/images/icons/logo.svg";
import { AddReportModal } from "../../../../components/reports/AddReportModal";
import patients from "../../../../api/patients";
import Skeleton from "../../../../components/skeleton";
import { submitPatientReport } from "./actions";
import { toastService } from "../../../../shared/utils/toast";
import {
  hideLoading,
  showLoading,
} from "../../../../connected-components/loader/reducer";

export const PatientDetailsPage: React.FC<PatientDetailsPageProps> = () => {
  const { id: patientId } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("overview");
  const [patient, setPatient] = useState<Patient | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshReports, setRefreshReports] = useState(0);
  const tabsContainerRef = useRef<HTMLDivElement>(null);

  const scrollTabs = (direction: "left" | "right") => {
    if (tabsContainerRef.current) {
      const scrollAmount = 200; // Adjust this value as needed
      const currentScroll = tabsContainerRef.current.scrollLeft;
      const newScroll =
        direction === "left"
          ? currentScroll - scrollAmount
          : currentScroll + scrollAmount;

      tabsContainerRef.current.scrollTo({
        left: newScroll,
        behavior: "smooth",
      });
    }
  };

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
    if (tabsContainerRef.current) {
      const tabElement = tabsContainerRef.current.querySelector(
        `[data-tab-id="${tabId}"]`,
      );
      if (tabElement) {
        tabElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  };

  React.useEffect(() => {
    const fetchPatientData = async () => {
      try {
        setIsLoading(true);
        if (!patientId) {
          throw new Error("Patient ID is required");
        }

        // Fetch patient data from API
        const response = await patients.getPatientById(patientId);
        const apiPatient = response.content;

        // Transform the API data to match the UI requirements
        const transformedPatient: Patient = {
          ...apiPatient,
          name: apiPatient.full_name,
          age: apiPatient.age,
          recentActivity: [], // Keep static for now
          vitalSignsData: {
            heartRate: [],
            oxygenSaturation: [],
            bloodPressure: {
              systolic: [],
              diastolic: [],
            },
            temperature: [],
          }, // Keep static for now
          reports: [], // Keep static for now
          medications: (apiPatient.medications || []).map((med) => ({
            id: med.id.toString(),
            name: med.name,
            dosage: med.dosage,
            frequency: med.frequency,
            startDate: med.start_date,
            endDate: med.end_date,
            status: med.is_currently_active ? "active" : "discontinued",
            notes: med.notes,
            warnings: [],
          })),
          appointments: [], // Keep static for now
        };

        setPatient(transformedPatient);
        setError(null);
      } catch (err) {
        setError("Failed to load patient data");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPatientData();
  }, [patientId]);

  const handleAddMedication = (medication: Medication) => {
    setPatient((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        medications: [...(prev.medications || []), medication],
      };
    });
  };

  const handleEditMedication = (medication: Medication) => {
    setPatient((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        medications: (prev.medications || []).map((m) =>
          m.id === medication.id ? medication : m,
        ),
      };
    });
  };

  const handleDiscontinueMedication = (medication: Medication) => {
    setPatient((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        medications: (prev.medications || []).map((m) =>
          m.id === medication.id ? medication : m,
        ),
      };
    });
  };

  const handleAddReport = async (values: any, form_id: number) => {
    dispatch(showLoading());
    const extra = values.extra_fields;
    const temp = { ...values };
    delete temp.extra_fields;
    delete temp.form_id;
    delete temp.reportType;
    delete temp.useAI;
    const form = {
      patient_id: parseInt(patientId || "0"),
      form_id,
      extra_fields: extra,
      values: temp,
    };
    const response = await submitPatientReport(form);
    if (response) {
      dispatch(hideModal());
      toastService.showSuccessToast("Report added successfully");
      setRefreshReports((r) => r + 1);
    }
    dispatch(hideLoading());
  };

  const openAddReportModal = () => {
    dispatch(
      showModal({
        type: ModalType.COMPONENT,
        title: "Add New Report",
        component: <AddReportModal onSubmit={handleAddReport} />,
      }),
    );
  };

  if (!patientId) {
    return <div className="p-4 text-red-500">Patient ID is required</div>;
  }

  if (isLoading) {
    return (
      <div className="flex overflow-hidden">
        <div className="flex-1 flex flex-col overflow-hidden lg:pr-80">
          <div className="flex-1 overflow-y-auto">
            <div className="lg:px-0 space-y-4 sm:space-y-6 py-4">
              {/* Patient Header Skeleton */}
              <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-darkCardBackground p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center space-x-4">
                    <Skeleton className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl" />
                    <div className="space-y-2">
                      <Skeleton className="h-6 w-48" />
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <Skeleton className="h-6 w-16" />
                    <Skeleton className="h-6 w-16" />
                    <Skeleton className="h-10 w-32" />
                  </div>
                </div>
              </div>

              {/* Tabs Navigation Skeleton */}
              <div className="bg-white dark:bg-darkCardBackground border border-gray-100 dark:border-gray-700 rounded-lg p-1">
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-1">
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} className="h-10" />
                  ))}
                </div>
              </div>

              {/* Tab Content Skeleton */}
              <div className="pb-4">
                <Skeleton className="h-96" />
              </div>
            </div>
          </div>
        </div>
        {/* Sidebar Skeleton for desktop */}
        <div className="hidden lg:flex flex-col w-80 border-l border-gray-100 dark:border-gray-700 bg-white dark:bg-darkCardBackground h-screen fixed right-0 top-0 z-50 animate-pulse">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <Skeleton className="w-8 h-8 rounded-full" />
              <Skeleton className="h-6 w-32" />
            </div>
          </div>
          <div className="flex-1 px-4 py-3 space-y-4 overflow-y-auto">
            <Skeleton className="h-20 w-full rounded-md" />
            <Skeleton className="h-28 w-full rounded-md" />
            <Skeleton className="h-28 w-full rounded-md" />
            <Skeleton className="h-32 w-full rounded-md" />
          </div>
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <Skeleton className="h-10 w-full rounded-md" />
            <Skeleton className="h-4 w-3/4 mt-2 mx-auto" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="p-4 text-red-500">{error}</div>;
  }

  if (!patient) {
    return <div className="p-4">Patient not found</div>;
  }

  const tabs = [
    { id: "overview", label: "Patient Demographics" },
    { id: "vitals", label: "Vitals" },
    { id: "reports", label: "Reports" },
    { id: "medications", label: "Medications" },
    { id: "clinical-notes", label: "Clinical Notes" },
    { id: "medical-history", label: "Medical History" },
    { id: "lab-results", label: "Lab Results" },
    { id: "immunizations", label: "Immunizations" },
    { id: "billing", label: "Billing & Coding" },
    { id: "orders", label: "Orders & Referrals" },
  ];

  return (
    <div className="flex overflow-hidden">
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden lg:pr-80">
        <div className="flex-1 overflow-y-auto">
          <div className="lg:px-0 space-y-4 sm:space-y-6 py-4">
            {/* Patient Header */}
            <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-darkCardBackground p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary rounded-xl flex items-center justify-center text-white">
                    <User className="w-6 h-6 sm:w-8 sm:h-8" />
                  </div>
                  <div>
                    <h2 className="text-xl sm:text-2xl font-medium dark:text-white">
                      {patient.name}
                    </h2>
                    <p className="text-sm sm:text-base text-gray-500 dark:text-gray-300">
                      {patient.age} years old • {patient.gender}
                      {patient.nationality && ` • ${patient.nationality}`}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  {patient.civil_status && (
                    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border border-primary text-primary dark:text-primary-300">
                      {patient.civil_status}
                    </span>
                  )}
                  <button
                    onClick={openAddReportModal}
                    className="inline-flex items-center justify-center rounded-md text-sm font-medium h-9 sm:h-10 px-3 sm:px-4 py-2 bg-primary dark:bg-primary text-white hover:bg-primary/90 dark:hover:bg-primary-400"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Report
                  </button>
                </div>
              </div>
            </div>

            {/* Tabs Navigation */}
            <div className="bg-white dark:bg-darkCardBackground border border-gray-100 dark:border-gray-700 rounded-lg p-1">
              <div className="flex items-center">
                <button
                  onClick={() => scrollTabs("left")}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div
                  ref={tabsContainerRef}
                  className="flex-1 overflow-x-auto scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                >
                  <div className="flex space-x-1 min-w-max">
                    {tabs.map((tab) => (
                      <button
                        key={tab.id}
                        data-tab-id={tab.id}
                        onClick={() => handleTabClick(tab.id)}
                        className={`py-2 px-3 text-sm font-medium rounded-md whitespace-nowrap ${
                          activeTab === tab.id
                            ? "bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-300"
                            : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => scrollTabs("right")}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Tab Content */}
            <div className="pb-4">
              {activeTab === "overview" && <OverviewTab patient={patient} />}
              {activeTab === "vitals" && (
                <VitalsTab vitalSignsData={patient.vitalSignsData} />
              )}
              {activeTab === "reports" && (
                <ReportsTab
                  patientId={parseInt(patientId)}
                  refreshKey={refreshReports}
                  onAddReport={() => console.log("Add report")}
                />
              )}
              {activeTab === "medications" && patient && (
                <MedicationsTab
                  patientId={parseInt(patientId)}
                  medications={patient.medications || []}
                  onAddMedication={handleAddMedication}
                  onEditMedication={handleEditMedication}
                  onDiscontinueMedication={handleDiscontinueMedication}
                />
              )}
              {activeTab === "clinical-notes" && <ClinicalNotesTab />}
              {activeTab === "medical-history" && <MedicalHistoryTab />}
              {activeTab === "lab-results" && <LabResultsTab />}
              {activeTab === "immunizations" && <ImmunizationsTab />}
              {activeTab === "billing" && <BillingTab patient={patient} />}
              {activeTab === "orders" && <OrdersTab />}
            </div>
          </div>
        </div>
      </div>

      {/* AI Sidebar - Single instance for both mobile and desktop */}
      <AISidebar patientName={patient.name} patientId={parseInt(patientId)} />

      {/* Mobile AI FAB Button */}
      <div className="lg:hidden">
        <button
          onClick={() => {
            const sidebar = document.getElementById("ai-sidebar");
            const fabButton = document.getElementById("ai-fab-button");
            if (sidebar) {
              sidebar.classList.remove("translate-x-full");
              sidebar.classList.add("translate-x-0");
              if (fabButton) {
                fabButton.classList.add("hidden");
              }
            }
          }}
          id="ai-fab-button"
          className="fixed bottom-6 right-6 w-14 h-14 rounded-md border border-solid border-primary/20 bg-white/40 dark:bg-white transition-all duration-300 flex items-center justify-center z-50 group"
        >
          <img
            src={logo}
            alt="Genio AI"
            className="w-8 h-8 transition-transform duration-300 group-hover:scale-110"
          />
          <span className="sr-only">Open AI Assistant</span>
        </button>
      </div>
    </div>
  );
};

export default PatientDetailsPage;
